import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, type WebApi } from '../api.js';
import type { AuthUser } from '../auth.js';
import { Button, Note, TextField } from '../ui/index.js';

/** Signed in, but in no org: redeem an invitation, or go buy a plan. */
export function NoOrgPage({ api, user, initialCode, pricingUrl, onJoined, onSignOut }: { api: WebApi; user: AuthUser; initialCode?: string; pricingUrl: string; onJoined: () => Promise<void>; onSignOut: () => void }) {
  const [code, setCode] = useState(initialCode ?? ''); const [error, setError] = useState<string | null>(null); const [busy, setBusy] = useState(false); const [starting, setStarting] = useState(false);
  const startTrial = async () => { setError(null); setStarting(true); try { await api.createOrg(); await onJoined(); } catch (err) { setError(err instanceof ApiError ? orgMessage(err) : String(err)); } finally { setStarting(false); } };
  useEffect(() => { if (initialCode) setCode(initialCode); }, [initialCode]);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    if (code.replace(/[^A-Za-z0-9]/g, '').length !== 12) { setError('Codes have 12 letters and digits.'); return; }
    setBusy(true);
    try { await api.acceptInvite(code); await onJoined(); }
    catch (err) { setError(err instanceof ApiError ? inviteMessage(err) : String(err)); }
    finally { setBusy(false); }
  };
  return (
    <main className="rv-signin">
      <div className="rv-signin__card">
        <h1 className="rv-display">No workspace yet</h1>
        <p className="rv-body">Signed in as {user.email ?? user.uid}.</p>
        <Button variant="primary" size="lg" icon="sparkles" busy={starting} disabled={busy} onClick={() => void startTrial()}>Start a free trial</Button>
        <p className="rv-body rv-muted">14 days, 300 credits, one machine. No card needed.</p>
        <p className="rv-or">or join one</p>
        <form onSubmit={submit} className="rv-stack" noValidate>
          <TextField label="Invitation code" value={code} onChange={(e) => setCode(e.currentTarget.value)} placeholder="XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false} hint="From the admin who invited you." />
          <Button type="submit" size="lg" busy={busy} disabled={starting}>Join workspace</Button>
        </form>
        {error ? <Note tone="danger">{error}</Note> : null}
        <div className="rv-row rv-row--between">
          <a className="rv-link" href={pricingUrl}>See the plans</a>
          <Button variant="ghost" icon="log-out" onClick={onSignOut}>Sign out</Button>
        </div>
      </div>
    </main>
  );
}
export function orgMessage(e: ApiError): string {
  switch (e.detail) {
    case 'email not verified': return 'Verify your email address first, then try again.';
    case 'token has no email': return 'Sign in with an email address to start a workspace.';
    case 'already a member of a workspace': return 'You already belong to a workspace. Reload the page.';
    case 'a workspace already uses this email': return 'A workspace already uses this address. Ask its admin for an invitation.';
    default: return e.message;
  }
}
export function inviteMessage(e: ApiError): string {
  switch (e.detail) {
    case 'invite mismatch': return 'That code is not right. Check it and try again.';
    case 'invite used': return 'That code was already used.';
    case 'invite expired': return 'That code has expired. Ask for a new one.';
    case 'invite revoked': return 'That invitation was withdrawn.';
    case 'invite email_mismatch': return 'This invitation is for a different email address.';
    case 'email not verified': return 'Verify your email address first, then try again.';
    case 'token has no email': return 'Sign in with an email address to accept an invitation.';
    case 'already a member of another org': return 'You already belong to another workspace.';
    default: return e.message;
  }
}
